// Tab / Shift+Tab switch the selected camera on the home scene, and a
// horizontal swipe on the camera panel does the same on touch screens.
// Kept pure so the rules are testable without a DOM.

/** The next id in `ids` after `current` (dir 1) or before it (dir -1), wrapping. */
export function cycleId(ids: readonly string[], current: string | null, dir: 1 | -1): string | null {
  if (ids.length === 0) return null;
  const i = current === null ? -1 : ids.indexOf(current);
  if (i === -1) return dir === 1 ? (ids[0] ?? null) : (ids[ids.length - 1] ?? null);
  const n = ids.length;
  return ids[(((i + dir) % n) + n) % n] ?? null;
}

/**
 * Where keyboard focus is when Tab is pressed:
 * - "idle": nothing interactive is focused (the page body or the scene itself)
 * - "feed": the camera feed in the panel
 * - "other": any real control (a ring node, a button, a field, a dialog)
 */
export type TabFocus = "idle" | "feed" | "other";

export interface TabInput {
  key: string;
  shiftKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  focus: TabFocus;
  /** A section panel, the radial menu or a dialog is open over the scene. */
  overlayOpen: boolean;
  cameraCount: number;
}

/**
 * Tab only switches cameras when focus is idle or on the feed, nothing is
 * open over the scene, and there is more than one camera. Everywhere else Tab
 * keeps its normal job of moving focus, so no one gets trapped (Escape on the
 * feed hands focus to the badge; see CameraPanel). Returns the direction to
 * move (Shift+Tab goes back) or null to let the browser handle Tab.
 */
export function tabDirection(e: TabInput): 1 | -1 | null {
  if (e.key !== "Tab" || e.ctrlKey || e.metaKey || e.altKey) return null;
  if (e.overlayOpen || e.cameraCount < 2 || e.focus === "other") return null;
  return e.shiftKey ? -1 : 1;
}

/** A swipe is horizontal, long enough and quick enough. Left swipe = next camera. */
export function swipeDirection(dx: number, dy: number, ms: number): 1 | -1 | null {
  if (ms > 800) return null;
  if (Math.abs(dx) < 40) return null;
  if (Math.abs(dx) < Math.abs(dy) * 1.5) return null;
  return dx < 0 ? 1 : -1;
}
